import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../store/store';
import { getLiteImages } from '../../../rtk-apis/reports/getLiteImages';
import { updateReportClips, AttachedClip } from '../../../rtk-apis/reports/updateReportClips';
import {
  LiteImageItem,
  getDisplayLabel,
  buildClipsFromLiteImages,
} from './attachClipsHelpers';

interface AttachClipsModalProps {
  open: boolean;
  reportId?: string | null;
  studyInstanceUID?: string | null;
  initialAttachedClips?: AttachedClip[];
  initialSharedClipsName?: string | null;
  onClose: () => void;
  onSaved: (clips: AttachedClip[], shareUrl: string | null, sharedClipsName: string) => void;
}

const isVideoUrl = (url?: string) => /\.(mp4|webm|ogg)(\?|#|$)/i.test(String(url || ''));
const isImageUrl = (url?: string) => /\.(png|jpe?g|gif|webp|bmp)(\?|#|$)/i.test(String(url || ''));

const AttachClipsModal: React.FC<AttachClipsModalProps> = ({
  open,
  reportId,
  studyInstanceUID,
  initialAttachedClips = [],
  initialSharedClipsName = '',
  onClose,
  onSaved,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [items, setItems] = useState<LiteImageItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [hasLoaded, setHasLoaded] = useState(false);
  const [selected, setSelected] = useState<Record<string, AttachedClip>>({});
  const [brokenMedia, setBrokenMedia] = useState<Record<string, boolean>>({});
  const [mediaMode, setMediaMode] = useState<Record<string, 'image' | 'video'>>({});
  const [mediaFailCount, setMediaFailCount] = useState<Record<string, number>>({});
  const [linkName, setLinkName] = useState<string>('');

  useEffect(() => {
    if (!open) return;
    setLinkName(initialSharedClipsName || '');
  }, [initialSharedClipsName, open]);

  useEffect(() => {
    if (!open) return;

    const seeded = (initialAttachedClips || []).reduce(
      (acc, clip) => {
        if (clip?.sopInstanceUID) {
          acc[clip.sopInstanceUID] = clip;
        }
        return acc;
      },
      {} as Record<string, AttachedClip>
    );
    setSelected(seeded);
  }, [initialAttachedClips, open]);

  useEffect(() => {
    if (!open) return;
    setHasLoaded(false);
    setItems([]);
    setBrokenMedia({});
    setMediaMode({});
    setMediaFailCount({});
    if (!studyInstanceUID) {
      setError('Missing StudyInstanceUID. Refresh page and try again.');
      return;
    }

    let mounted = true;
    setLoading(true);
    setError('');

    dispatch(getLiteImages({ studyInstanceUID }))
      .unwrap()
      .then(response => {
        if (!mounted) return;
        const fetchedItems = Array.isArray(response?.data) ? response.data : [];
        setItems(fetchedItems);
        // Default behavior: pre-select all clips on modal open. Built by the same
        // helper the automatic attach uses, so opening the modal on an auto-attached
        // report shows exactly the set that was saved rather than renaming it.
        setSelected(prev => {
          const next = { ...prev };
          buildClipsFromLiteImages(fetchedItems).forEach(clip => {
            if (!next[clip.sopInstanceUID]) {
              next[clip.sopInstanceUID] = clip;
            }
          });
          return next;
        });
      })
      .catch(err => {
        if (!mounted) return;
        setError(err?.msg || err?.message || 'Failed to fetch study clips');
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
        setHasLoaded(true);
      });

    return () => {
      mounted = false;
    };
  }, [dispatch, open, studyInstanceUID]);

  const selectedCount = useMemo(() => Object.keys(selected).length, [selected]);

  const toggleSelection = (item: LiteImageItem, isChecked: boolean) => {
    setSelected(prev => {
      const next = { ...prev };
      if (isChecked) {
        next[item.sopInstanceUID] = {
          sopInstanceUID: item.sopInstanceUID,
          url: item.url,
          name: getDisplayLabel(item, 0),
        };
      } else {
        delete next[item.sopInstanceUID];
      }
      return next;
    });
  };

  const updateName = (sopInstanceUID: string, name: string) => {
    setSelected(prev => ({
      ...prev,
      [sopInstanceUID]: {
        ...prev[sopInstanceUID],
        name,
      },
    }));
  };

  const getInitialMode = (item: LiteImageItem) => {
    const src = item.thumbnailUrl || item.url;
    if (item.thumbnailUrl) return 'image' as const;
    if (isImageUrl(src)) return 'image' as const;
    if (isVideoUrl(src)) return 'video' as const;
    return 'video' as const;
  };

  const handleMediaError = (item: LiteImageItem) => {
    const uid = item.sopInstanceUID;
    const currentMode = mediaMode[uid] || getInitialMode(item);
    const nextCount = (mediaFailCount[uid] || 0) + 1;

    setMediaFailCount(prev => ({ ...prev, [uid]: nextCount }));

    if (nextCount === 1) {
      setMediaMode(prev => ({
        ...prev,
        [uid]: currentMode === 'image' ? 'video' : 'image',
      }));
      return;
    }

    setBrokenMedia(prev => ({ ...prev, [uid]: true }));
  };

  const handleSave = async () => {
    setError('');
    if (!reportId) {
      setError('Save the report once before attaching clips.');
      return;
    }

    const attachedClips = Object.values(selected).map(clip => ({
      ...clip,
      name: clip.name?.trim(),
    }));

    if (attachedClips.some(clip => !clip.name || !clip.url || !clip.sopInstanceUID)) {
      setError('Every selected clip requires a name.');
      return;
    }

    try {
      setSaving(true);
      const trimmedLinkName = linkName.trim();
      const result = await dispatch(updateReportClips({ reportId, attachedClips, sharedClipsName: trimmedLinkName })).unwrap();
      const shareUrl = result?.data?.sharedClipsUrl || null;
      onSaved(attachedClips, shareUrl, trimmedLinkName);
      onClose();
    } catch (err: any) {
      setError(err?.msg || err?.message || 'Failed to save attached clips');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="attach-clips-modal-overlay">
      <div className="attach-clips-modal">
        <div className="attach-clips-modal__header">
          <div className="attach-clips-modal__title-wrap">
            <h3>Change Study Clips</h3>
            <span className="attach-clips-modal__count">Selected: {selectedCount}</span>
          </div>
          <button type="button" onClick={onClose} disabled={saving} aria-label="Close">
            ×
          </button>
        </div>

        <div className="attach-clips-modal__link-name">
          <label htmlFor="attach-clips-link-name">Link Name</label>
          <input
            id="attach-clips-link-name"
            type="text"
            value={linkName}
            onChange={e => setLinkName(e.target.value)}
            placeholder="e.g. Stress Echo Clips"
            disabled={saving}
          />
        </div>

        {loading ? (
          <div className="attach-clips-loading">
            <div className="attach-clips-loading__spinner" />
            <p>Fetching clips...</p>
          </div>
        ) : null}
        {!loading && hasLoaded && items.length === 0 ? <p>No lite clips found for this study.</p> : null}

        {items.length > 0 ? (
          <div className="attach-clips-list">
            {items.map((item, index) => {
              const isChecked = !!selected[item.sopInstanceUID];
              const thumbnailSrc = item.thumbnailUrl || item.url;
              const displayLabel = getDisplayLabel(item, index);
              const showBroken = !!brokenMedia[item.sopInstanceUID];
              const mode = mediaMode[item.sopInstanceUID] || getInitialMode(item);
              const showVideo = !showBroken && mode === 'video';

              return (
                <div key={item.sopInstanceUID} className="attach-clips-item">
                  <label className="attach-clips-item__checkbox">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={e => toggleSelection(item, e.target.checked)}
                      disabled={saving}
                    />
                    <span title={item.sopInstanceUID}>{displayLabel}</span>
                  </label>
                  <a href={item.url} target="_blank" rel="noreferrer" className="attach-clips-item__media">
                    {showVideo ? (
                      <video
                        src={thumbnailSrc}
                        muted
                        playsInline
                        preload="metadata"
                        autoPlay={false}
                        controls
                        onLoadedData={e => {
                          e.currentTarget.pause();
                          e.currentTarget.currentTime = 0;
                        }}
                        onError={() => handleMediaError(item)}
                      />
                    ) : !showBroken ? (
                      <img
                        src={thumbnailSrc}
                        alt={displayLabel}
                        onError={() => handleMediaError(item)}
                      />
                    ) : (
                      <div className="attach-clips-item__fallback">Preview unavailable</div>
                    )}
                  </a>
                  {isChecked ? (
                    <input
                      type="text"
                      value={selected[item.sopInstanceUID]?.name || ''}
                      onChange={e => updateName(item.sopInstanceUID, e.target.value)}
                      placeholder="Enter clip name"
                      disabled={saving}
                    />
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}

        {error ? <p className="attach-clips-modal__error">{error}</p> : null}

        <div className="attach-clips-modal__footer">
          <div className="attach-clips-modal__actions">
            <button type="button" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="button" onClick={handleSave} disabled={saving || loading}>
              {saving ? 'Saving...' : 'Save Clips'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttachClipsModal;
